import { useQuery } from '@tanstack/react-query';
import { filter, find, findIndex, first, flatMap, includes, isEmpty, isNumber, map, size, some } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { ArrayParam, BooleanParam, JsonParam, NumberParam, StringParam, useQueryParam } from 'use-query-params';

import { getCaseSlideThumbnailsData, getProcedureIds } from 'api/procedures';
import { getSlidesViewerNavigationPrefetchData } from 'api/slides';
import { CaseSlideThumbnailData } from 'interfaces/procedure';
import { SlidePrefetchData } from 'interfaces/slide';
import { useCasesParams } from 'utils/useCasesParams';
import { CasesParams } from 'utils/useCasesParams/types';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { DEFAULT_PAGE_SIZE } from 'utils/useEncodedFilters/constants';
import { encodeQueryParamsUsingSchema } from 'utils/useEncodedFilters/helpers';
import { ExperimentResultsSelection } from 'utils/useEncodedFilters/types';
import { usePaginatedQueries } from 'utils/usePaginatedQueries';
import usePrevious from 'utils/usePrevious';

const slidesViewerNavigationPrefetchSchema = {
  filters: JsonParam,
  features: JsonParam,
  clinicalData: JsonParam,
  labId: StringParam,
  page: NumberParam,
  pageSize: NumberParam,
  sortBy: JsonParam,
  resultsMode: StringParam,
  experimentResultsSelection: StringParam,
  caseIdsToInclude: ArrayParam,
  caseIdsToExclude: ArrayParam,
  slideIdsToInclude: ArrayParam,
  slideIdsToExclude: ArrayParam,
  datasetJobFilters: JsonParam,
  datasetJobFiltersIncludeExclude: StringParam,
  slidesMode: BooleanParam,
  skipPagination: BooleanParam,
  pendingSlidesMode: BooleanParam,
};

export const useFilteredCaseIds = (caseId: number, studyId?: string) => {
  const { casesParams } = useCasesParams();
  const { generateEncodedParams } = useEncodedFilters({
    experimentResultsSelection: ExperimentResultsSelection.OnlyQAFailed,
  });

  const idsEncodedParams = generateEncodedParams({
    filters: {
      ...casesParams?.filters,
      ...(studyId ? { studyId } : {}),
    },
    // The page is not important, the response return all the ids
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const queryKey = ['procedureIds', studyId ? [idsEncodedParams] : [idsEncodedParams, caseId]];

  return useQuery(queryKey, {
    queryFn: ({ signal }) => getProcedureIds(idsEncodedParams, studyId ? undefined : caseId, signal),
    enabled: Boolean(studyId || caseId),
    keepPreviousData: true,
    retry: false,
  });
};

export const useFilteredCaseSlideThumbnailsData = ({
  enabled = true,
  casesParams,
}: {
  enabled?: boolean;
  casesParams: CasesParams;
}) => {
  return useQuery(['slideThumbnailsData', [casesParams]], {
    queryFn: ({ signal }) => getCaseSlideThumbnailsData(casesParams, signal),
    keepPreviousData: true,
    enabled,
    retry: false,
  });
};

export const useFilteredSlidesViewerNavigationPrefetchData = ({
  studyId,
  enabled = true,
  numSurroundingPagesToPrefetch: numSurroundingPagesToPrefetchInput = 1,
  focusedCaseId,
  focusedSlideId,
  debug = false,
}: {
  studyId?: string;
  enabled?: boolean;
  numSurroundingPagesToPrefetch?: number;
  focusedCaseId?: number;
  focusedSlideId?: string;
  debug?: boolean;
} = {}): {
  data: SlidePrefetchData[];
  isLoading: boolean;
  isInitialLoading: boolean;
} => {
  const [pendingSlidesMode] = useQueryParam('pendingSlidesMode', BooleanParam);

  const missingFocusInput = !focusedCaseId && !focusedSlideId;

  const { queryParams } = useEncodedFilters({
    experimentResultsSelection: ExperimentResultsSelection.OnlyQAFailed,
  });

  const { casesParams } = useCasesParams();

  const allSlideThumbnailsQuery = useFilteredCaseSlideThumbnailsData({ casesParams, enabled });
  const slideThumbnailsData = useMemo(
    () => filter(allSlideThumbnailsQuery?.data, ({ caseId }) => (pendingSlidesMode ? caseId == null : caseId !== null)),
    [allSlideThumbnailsQuery?.data, pendingSlidesMode]
  );

  const pageSize = DEFAULT_PAGE_SIZE;
  const [currentPageState, setCurrentPage] = useState<number | null>(null);
  const currentPage = currentPageState ?? 1;
  const [numSurroundingPagesToPrefetchState, setNumSurroundingPagesToPrefetchState] = useState<number | null>(null);
  const numSurroundingPagesToPrefetch = numSurroundingPagesToPrefetchState ?? numSurroundingPagesToPrefetchInput;

  const cantGetCurrentPage = enabled && !isNumber(focusedCaseId) && !focusedSlideId && !isEmpty(slideThumbnailsData);
  const previousCanGetCurrentPage = usePrevious(cantGetCurrentPage);

  useEffect(() => {
    if (debug) {
      console.debug('useFilteredSlidesViewerNavigationPrefetchData - recalculating currentPage', {
        focusedCaseId,
        focusedSlideId,
        currentPage,
        pageSize,
        pendingSlidesMode,
        numSurroundingPagesToPrefetch,
        numSurroundingPagesToPrefetchInput,
        allSlideThumbnailsQuery,
        studyId,
        enabled,
      });
    }
    if (!enabled || isEmpty(slideThumbnailsData) || allSlideThumbnailsQuery?.isLoading) {
      return;
    }

    if (cantGetCurrentPage) {
      // To avoid spamming the console with the same warning
      if (previousCanGetCurrentPage !== cantGetCurrentPage && !missingFocusInput) {
        console.warn("No case or slides in the page to prefetch - can't determine the current page", {
          slideThumbnailsData,
          focusedCaseId,
          focusedSlideId,
          currentPage,
          pageSize,
          pendingSlidesMode,
          numSurroundingPagesToPrefetch,
          numSurroundingPagesToPrefetchInput,
          allSlideThumbnailsQuery,
          studyId,
          enabled,
        });
      }
      return;
    }
    let pagesMatchingCaseOrSlides: number[] = [];
    const matchingResults: Partial<CaseSlideThumbnailData>[] = filter(
      slideThumbnailsData,
      focusedSlideId
        ? isNumber(focusedCaseId)
          ? { caseId: focusedCaseId, slideId: focusedSlideId }
          : { slideId: focusedSlideId }
        : { caseId: focusedCaseId }
    );
    const matchingIndices = filter(
      map(matchingResults, (pattern) => findIndex(slideThumbnailsData, pattern)),
      (index) => index >= 0
    );
    if (isEmpty(matchingIndices)) {
      console.warn(
        `No case or slides in the page to prefetch - no matching case or slides found, prefetching page ${currentPage}`,
        { focusedCaseId, focusedSlideId, matchingResults, slideThumbnailsData }
      );
      return;
    } else {
      pagesMatchingCaseOrSlides = map(matchingIndices, (index) => Math.floor(index / pageSize) + 1);
    }
    if (!isEmpty(pagesMatchingCaseOrSlides) && !includes(pagesMatchingCaseOrSlides, currentPage)) {
      if (isNumber(currentPageState)) {
        console.info(
          `Current page ${currentPage} does not match any of the pages with the case or slides in the page. Prefetching the first page with the case or slides.`,
          { currentPage, pagesMatchingCaseOrSlides }
        );
      }
      const firstMatchingPage = first(pagesMatchingCaseOrSlides);
      if (isNumber(firstMatchingPage)) {
        setCurrentPage(firstMatchingPage);
      }

      // If the number of surrounding pages to prefetch is less than the number of pages requested, update the state
      // to reflect the number of pages that need to be prefetched.
      if (size(pagesMatchingCaseOrSlides) > numSurroundingPagesToPrefetch) {
        setNumSurroundingPagesToPrefetchState(size(pagesMatchingCaseOrSlides));
      }
    } else {
      setNumSurroundingPagesToPrefetchState(null);
    }
  }, [
    enabled,
    slideThumbnailsData,
    cantGetCurrentPage,
    focusedCaseId,
    focusedSlideId,
    numSurroundingPagesToPrefetchInput,
    pageSize,
  ]);

  const filters = {
    ...casesParams?.filters,
    ...(studyId && !casesParams?.filters?.studyId ? { studyId: studyId } : {}),
  };
  const getSlidesViewerNavigationPrefetchArgs = (page: number) =>
    encodeQueryParamsUsingSchema(
      {
        ...(!isEmpty(filters) ? { filters } : {}),
        ...(!isEmpty(queryParams?.features) ? { features: queryParams.features } : {}),
        ...(!isEmpty(queryParams?.clinicalData) ? { clinicalData: queryParams.clinicalData } : {}),
        ...(queryParams?.labId ? { labId: queryParams.labId } : {}),
        page: page + 1,
        pageSize,
        ...(!isEmpty(queryParams?.sortBy) ? { sortBy: queryParams.sortBy } : {}),
        ...(queryParams?.resultsMode ? { resultsMode: queryParams.resultsMode } : {}),
        experimentResultsSelection: ExperimentResultsSelection.OnlyQAFailed,
        ...(queryParams?.caseIdsToInclude?.length ? { caseIdsToInclude: queryParams.caseIdsToInclude } : {}),
        ...(casesParams?.caseIdsToExclude?.length ? { caseIdsToExclude: casesParams.caseIdsToExclude } : {}),
        ...(queryParams?.slideIdsToInclude?.length ? { slideIdsToInclude: queryParams.slideIdsToInclude } : {}),
        ...(casesParams?.slideIdsToExclude?.length ? { slideIdsToExclude: casesParams.slideIdsToExclude } : {}),
        ...(!isEmpty(queryParams?.datasetJobFilters) ? { datasetJobFilters: queryParams.datasetJobFilters } : {}),
        ...(queryParams?.datasetJobFiltersIncludeExclude
          ? { datasetJobFiltersIncludeExclude: queryParams.datasetJobFiltersIncludeExclude }
          : {}),
        ...(queryParams?.slidesMode ? { slidesMode: true } : {}),
        ...(queryParams?.skipPagination ? { skipPagination: true } : {}),
        ...(pendingSlidesMode ? { pendingSlidesMode: true } : {}),
      },
      slidesViewerNavigationPrefetchSchema,
      { arrayFormat: 'repeat' }
    );

  const totalSlides = size(slideThumbnailsData);
  const totalPages = Math.ceil(totalSlides / pageSize);

  const paginatedQueries = usePaginatedQueries({
    debug,
    consoleOnly: !debug,
    queriedEntityName: 'slides viewer navigation prefetch data',
    retry: false,
    pageQueryFn: (pageArgs, { signal }) => getSlidesViewerNavigationPrefetchData(pageArgs, signal),
    getPageArgs: getSlidesViewerNavigationPrefetchArgs,
    getQueryKeyForPage: (page) => ['slidesViewerNavigationPrefetchData', getSlidesViewerNavigationPrefetchArgs(page)],
    currentPage,
    totalPages,
    numSurroundingPagesToPrefetch,
  });

  const data = useMemo(() => {
    if (debug) {
      console.debug('useFilteredSlidesViewerNavigationPrefetchData - recalculating data', {
        paginatedQueries,
        slideThumbnailsData,
      });
    }
    const allPaginatedItems = flatMap(paginatedQueries.items, ({ items }) => items);
    return map(
      slideThumbnailsData,
      (caseThumbnailsData): SlidePrefetchData =>
        find(allPaginatedItems, {
          caseId: caseThumbnailsData.caseId,
          slideId: caseThumbnailsData.slideId,
        }) || caseThumbnailsData
    );
  }, [paginatedQueries.items, slideThumbnailsData]);

  const isLoading =
    allSlideThumbnailsQuery.isLoading || some(paginatedQueries.queries, (query) => query.isLoading && query.isFetching);
  const isInitialLoading =
    allSlideThumbnailsQuery.isInitialLoading || some(paginatedQueries.queries, 'isInitialLoading');

  const res = { data, isLoading, isInitialLoading };

  return res;
};
